import { useState, useEffect } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function DynamicLessonViewer() {
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/curriculum/subjects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubjects(response.data.subjects);
    } catch (err) {
      console.error("Error fetching subjects:", err);
    }
  };

  const generateLesson = async () => {
    if (!selectedSubject || !selectedGrade || !selectedTopic) {
      setError("Please select subject, grade level, and topic");
      return;
    }

    setLoading(true);
    setError("");
    setLesson(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/curriculum/lesson/${encodeURIComponent(selectedSubject.name)}/${selectedGrade}/${encodeURIComponent(selectedTopic)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLesson(response.data.lesson);
    } catch (err) {
      setError(err.response?.data?.detail || "Error generating lesson");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card" data-testid="dynamic-lesson-viewer">
        <h3>📚 Dynamic Curriculum Library</h3>
        <p style={{ color: "#FFD700", marginBottom: "20px" }}>
          Choose any subject and topic - curriculum generated in real-time from current internet resources!
        </p>

        {/* Subject Selection */}
        <div style={{ marginBottom: "20px" }}>
          <label style={{ display: "block", marginBottom: "8px", color: "#FFD700" }}>
            1. Select Subject:
          </label>
          <select
            value={selectedSubject?.id || ""}
            onChange={(e) => {
              const subject = subjects.find(s => s.id === e.target.value);
              setSelectedSubject(subject);
              setSelectedGrade("");
              setSelectedTopic("");
              setLesson(null);
            }}
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: "6px",
              border: "2px solid #333",
              background: "#222",
              color: "#eee",
              fontSize: "1rem"
            }}
          >
            <option value="">-- Choose a Subject --</option>
            {subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.name}
              </option>
            ))}
          </select>
          {selectedSubject && (
            <p style={{ margin: "10px 0 0 0", color: "#aaa", fontSize: "0.9rem" }}>
              {selectedSubject.description}
            </p>
          )}
        </div>

        {/* Grade Level Selection */}
        {selectedSubject && (
          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", marginBottom: "8px", color: "#FFD700" }}>
              2. Select Grade Level:
            </label>
            <select
              value={selectedGrade}
              onChange={(e) => {
                setSelectedGrade(e.target.value);
                setSelectedTopic("");
                setLesson(null);
              }}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "6px",
                border: "2px solid #333",
                background: "#222",
                color: "#eee",
                fontSize: "1rem"
              }}
            >
              <option value="">-- Choose Grade Level --</option>
              {selectedSubject.grade_levels.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Topic Selection */}
        {selectedSubject && selectedGrade && (
          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", marginBottom: "8px", color: "#FFD700" }}>
              3. Select Topic:
            </label>
            <select
              value={selectedTopic}
              onChange={(e) => {
                setSelectedTopic(e.target.value);
                setLesson(null);
              }}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "6px",
                border: "2px solid #333",
                background: "#222",
                color: "#eee",
                fontSize: "1rem"
              }}
            >
              <option value="">-- Choose a Topic --</option>
              {selectedSubject.topics.map((topic, index) => (
                <option key={index} value={topic}>
                  {topic}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Generate Button */}
        {selectedSubject && selectedGrade && selectedTopic && (
          <button
            onClick={generateLesson}
            disabled={loading}
            data-testid="generate-lesson-button"
            style={{ marginBottom: "20px" }}
          >
            {loading ? "🔄 Generating Lesson from Internet..." : "✨ Generate Lesson Content"}
          </button>
        )}

        {error && <div className="alert alert-error">{error}</div>}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <div className="spinner"></div>
            <p style={{ color: "#FFD700", marginTop: "20px" }}>
              🌐 Searching internet for current resources...
              <br />
              📖 Generating curriculum with Yisrael perspective...
              <br />
              ⚡ This may take 10-20 seconds...
            </p>
          </div>
        )}
      </div>

      {/* Display Generated Lesson */}
      {lesson && !loading && (
        <div className="card" style={{ background: "#1a1a1a" }} data-testid="generated-lesson">
          <div style={{
            background: "#1a4d1a",
            padding: "15px",
            borderRadius: "6px",
            marginBottom: "20px",
            border: "2px solid #90EE90"
          }}>
            <p style={{ margin: 0, color: "#90EE90" }}>
              ✅ <strong>Lesson Generated Successfully!</strong>
              <br />
              <span style={{ fontSize: "0.9rem" }}>
                Content sourced from current internet resources and free public materials
              </span>
            </p>
          </div>

          <div
            style={{
              background: "#222",
              padding: "20px",
              borderRadius: "8px",
              maxHeight: "600px",
              overflowY: "auto",
              color: "#eee",
              lineHeight: "1.6"
            }}
          >
            <ReactMarkdown>{lesson.content}</ReactMarkdown>
          </div>

          <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
            <button
              onClick={() => {
                setLesson(null);
                setSelectedTopic("");
              }}
              style={{ width: "auto", flex: 1 }}
            >
              📚 Choose Another Topic
            </button>
            <button
              onClick={() => window.print()}
              style={{ width: "auto", flex: 1 }}
            >
              🖨️ Print Lesson
            </button>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="card" style={{ background: "#0a0a0a", marginTop: "20px" }}>
        <h4 style={{ color: "#FFD700", marginBottom: "10px" }}>
          ℹ️ How Dynamic Curriculum Works
        </h4>
        <ul style={{ margin: 0, paddingLeft: "20px", color: "#aaa" }}>
          <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
            <strong style={{ color: "#eee" }}>Always Current:</strong> Content generated from latest internet resources
          </li>
          <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
            <strong style={{ color: "#eee" }}>Free Resources:</strong> All lessons reference free public materials
          </li>
          <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
            <strong style={{ color: "#eee" }}>Hebrew Perspective:</strong> Every lesson connects to Yisrael identity
          </li>
          <li style={{ background: "transparent", border: "none", padding: "0" }}>
            <strong style={{ color: "#eee" }}>Unlimited Topics:</strong> Can generate lessons on ANY subject or topic
          </li>
        </ul>
      </div>
    </div>
  );
}

export default DynamicLessonViewer;
